/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them, and displays a fallback UI instead of crashing the entire app.
 *
 * Critical for preventing blank pages when errors occur.
 *
 * @module components/ErrorBoundary
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary - catches and displays errors instead of blank pages
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleClearAndReload = (): void => {
    // Clear localStorage/sessionStorage and reload
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Ignore storage errors
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div
          style={{
            padding: 40,
            color: '#ff6b6b',
            background: '#2b3135',
            minHeight: '100vh',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <h1 style={{ marginBottom: 20, color: '#ff6b6b' }}>
            Something went wrong
          </h1>

          <div
            style={{
              background: '#1a1d1f',
              padding: 20,
              borderRadius: 8,
              marginBottom: 20,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#e0e0e0',
                fontSize: 14,
                margin: 0,
              }}
            >
              {this.state.error?.message || 'Unknown error'}
            </pre>
          </div>

          {this.state.error?.stack && (
            <details style={{ marginBottom: 20 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  color: '#9caa8c',
                  marginBottom: 10,
                }}
              >
                Stack trace
              </summary>
              <pre
                style={{
                  background: '#1a1d1f',
                  padding: 15,
                  borderRadius: 8,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 300,
                  color: '#888',
                }}
              >
                {this.state.error.stack}
              </pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '12px 24px',
                fontSize: 16,
                background: '#9caa8c',
                color: '#1a1d1f',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Reload Page
            </button>

            <button
              onClick={this.handleClearAndReload}
              style={{
                padding: '12px 24px',
                fontSize: 16,
                background: 'transparent',
                color: '#9caa8c',
                border: '1px solid #9caa8c',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Clear Cache & Reload
            </button>
          </div>

          <p
            style={{
              marginTop: 30,
              color: '#666',
              fontSize: 14,
            }}
          >
            If this error persists, try clearing your browser cache or contact support.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
